import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Crown,
  CheckCircle,
  Lock,
  ArrowUpRight,
  Sparkles,
  Users,
  Monitor,
  Loader,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// Tier badge colors
const TIER_CONFIG = {
  basic: {
    color: "bg-slate-500",
    textColor: "text-slate-100",
    borderColor: "border-slate-400",
    icon: null,
    label: "Basic",
  },
  professional: {
    color: "bg-blue-500",
    textColor: "text-blue-100",
    borderColor: "border-blue-400",
    icon: Sparkles,
    label: "Professional",
  },
  enterprise: {
    color: "bg-purple-500",
    textColor: "text-purple-100",
    borderColor: "border-purple-400",
    icon: Crown,
    label: "Enterprise",
  },
  standalone: {
    color: "bg-amber-500",
    textColor: "text-amber-100",
    borderColor: "border-amber-400",
    icon: Crown,
    label: "Standalone",
  },
};

// Feature display names
const FEATURE_NAMES = {
  fingerprint_capture: "Fingerprint Capture",
  fd258_cards: "FD-258 Card Generation",
  fd1164_cards: "FD-1164 Card Generation",
  local_database: "Local Database Storage",
  encryption: "AES-256 Encryption",
  id_verification: "ID Verification (OCR)",
  offline_mode: "USB/Offline Mode",
  cloud_sync: "Cloud Storage & Sync",
  multi_device: "Multi-Device Access",
  ebts_export: "EBTS Export",
  wsq_compression: "WSQ Compression",
  real_time_backup: "Real-time Backup",
  lan_installation: "LAN Installation",
  unlimited_users: "Unlimited Users",
  custom_branding: "Custom Branding",
  white_label: "White-Label",
  api_access: "API Access",
  dedicated_support: "Dedicated Support",
  sla_guarantee: "SLA Guarantee",
  on_site_training: "On-Site Training",
  priority_support: "Priority Support (24hr)",
};

const MySubscription = ({ onUpgradeClick }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get("/subscription");
      setSubscription(response.data);
    } catch (err) {
      console.error("Failed to fetch subscription:", err);
      setError("Failed to load subscription info");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-6" data-testid="subscription-loading">
        <div className="flex items-center justify-center gap-2 text-blue-200">
          <Loader className="w-5 h-5 animate-spin" />
          <span>Loading subscription...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 backdrop-blur-md border border-red-400/30 rounded-lg p-6" data-testid="subscription-error">
        <div className="flex items-center gap-2 text-red-300">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const tier = subscription?.tier || "basic";
  const tierConfig = TIER_CONFIG[tier] || TIER_CONFIG.basic;
  const TierIcon = tierConfig.icon;
  const enabledFeatures = subscription?.enabled_features || [];
  const features = subscription?.features || {};
  const limits = subscription?.limits || {};

  // Separate enabled and locked features
  const lockedFeatures = Object.entries(features)
    .filter(([_, info]) => !info.enabled)
    .map(([name, info]) => ({ name, ...info }));

  return (
    <div 
      className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg overflow-hidden"
      data-testid="my-subscription"
    >
      {/* Header */}
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${tierConfig.color} rounded-lg flex items-center justify-center`}>
              {TierIcon ? (
                <TierIcon className="w-5 h-5 text-white" />
              ) : (
                <Crown className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-sm font-heading font-semibold text-white">My Subscription</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`px-2 py-0.5 text-xs font-semibold rounded ${tierConfig.color} ${tierConfig.textColor}`}>
                  {tierConfig.label.toUpperCase()}
                </span>
                {tier !== "enterprise" && tier !== "standalone" && (
                  <span className="text-xs text-blue-200/60">
                    {subscription?.upgrade_available ? "Upgrade available" : ""}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {subscription?.upgrade_available && (
            <Link
              to="/license"
              className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-medium rounded-lg hover:from-cyan-400 hover:to-blue-400 transition"
              data-testid="upgrade-btn"
            >
              <ArrowUpRight className="w-4 h-4" />
              Upgrade
            </Link>
          )}
        </div>
      </div>

      {/* Limits */}
      <div className="px-5 py-3 bg-white/5 border-b border-white/10">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-300" />
            <span className="text-blue-200/70">Users:</span>
            <span className="text-white font-medium">{limits.max_users}</span>
          </div>
          <div className="flex items-center gap-2">
            <Monitor className="w-4 h-4 text-blue-300" />
            <span className="text-blue-200/70">Workstations:</span>
            <span className="text-white font-medium">{limits.max_workstations}</span>
          </div>
        </div>
      </div>

      {/* Features Summary */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-blue-200/70">
            <span className="text-white font-medium">{enabledFeatures.length}</span> features enabled
            {lockedFeatures.length > 0 && (
              <span className="ml-1">
                • <span className="text-amber-400">{lockedFeatures.length}</span> locked
              </span>
            )}
          </span>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition"
            data-testid="toggle-features-btn"
          >
            {expanded ? "Hide" : "Show"} all features
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {/* Enabled Features (collapsed view - show first 4) */}
        {!expanded && (
          <div className="flex flex-wrap gap-2">
            {enabledFeatures.slice(0, 4).map((feature) => (
              <span
                key={feature}
                className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-300 text-xs rounded"
              >
                <CheckCircle className="w-3 h-3" />
                {FEATURE_NAMES[feature] || feature.replace(/_/g, " ")}
              </span>
            ))}
            {enabledFeatures.length > 4 && (
              <span className="px-2 py-1 text-xs text-blue-200/60">
                +{enabledFeatures.length - 4} more
              </span>
            )}
          </div>
        )}

        {/* Expanded view - all features */}
        {expanded && (
          <div className="space-y-4">
            {/* Enabled Features */}
            <div>
              <h4 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">
                Enabled Features
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {enabledFeatures.map((feature) => (
                  <div
                    key={feature}
                    className="flex items-center gap-2 text-sm text-white/90"
                  >
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="truncate">{FEATURE_NAMES[feature] || feature.replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Locked Features */}
            {lockedFeatures.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">
                  Locked Features
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {lockedFeatures.map((feature) => (
                    <div
                      key={feature.name}
                      className="flex items-center gap-2 text-sm text-white/50 group cursor-pointer"
                      onClick={() => onUpgradeClick?.(feature.name, feature.required_tier)}
                      data-testid={`locked-feature-${feature.name}`}
                    >
                      <Lock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span className="truncate group-hover:text-white/70 transition">
                        {FEATURE_NAMES[feature.name] || feature.name.replace(/_/g, " ")}
                      </span>
                      <span className="text-xs text-amber-500/70 ml-auto">
                        {feature.required_tier}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubscription;
